/**
 * Sekmeler arasi iletisim ve akis LIDERI secimi.
 *
 * ## Neden gerekli: tarayicinin 6 baglanti siniri GERCEK
 *
 * uvicorn HTTP/1.1 konusuyor ve tarayicilar origin basina ~6 eszamanli
 * baglantiya izin veriyor. Dort sekme acikken her biri kendi SSE akisini
 * acarsa, kalan iki baglantiyla `/api/conversations`, `/api/attachments` ve
 * diger her sey yarisiyor - uygulama "yavas" degil TAKILMIS gorunuyor.
 *
 * Cozum: her aktif run icin TEK sekme lider oluyor ve akisi o acıyor;
 * takipciler HIC baglanti acmiyor, delta'lari `BroadcastChannel` uzerinden
 * aliyor.
 *
 * ## Optimizasyon GUVENLI sekilde basarisiz olabiliyor
 *
 * `navigator.locks` yoksa (Safari'nin eski surumleri) veya lider sekme
 * olurse (kilit otomatik serbest kaliyor, baska sekme aliyor ve kendi
 * `lastSeq`inden devam ediyor) her sekme BAGIMSIZ akitiyor. Bu **dogru,
 * yalnizca israf** - cunku sunucu resume icin nasilsa run basina cok
 * aboneyi desteklemek ZORUNDA. Bir optimizasyon icin dogru ozellik bu:
 * bozuldugunda uygulama calismaya devam ediyor.
 */

export type TabMessage =
  /** Lider bir metin parcasi aldi. */
  | { type: 'run_delta'; runId: string; text: string; seq: number }
  /** Lider run'in durumunu ogrendi. */
  | { type: 'run_status'; runId: string; status: string }
  /** Kuyruk pozisyonu degisti. */
  | { type: 'run_queue'; runId: string; position: number | null }
  /** Konusma listesi bayatladi. */
  | { type: 'conversations_invalidate' }
  /** Bir konusmanin mesajlari bayatladi. */
  | { type: 'messages_invalidate'; conversationId: string }
  /** Baska bir sekmede cikis yapildi. */
  | { type: 'logged_out' }

const CHANNEL = 'hedefai'

let channel: BroadcastChannel | null = null

function get(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!channel) channel = new BroadcastChannel(CHANNEL)
  return channel
}

/** Diger sekmelere yayin yapar. Kanal yoksa SESSIZCE gecer. */
export function publish(message: TabMessage): void {
  get()?.postMessage(message)
}

/** Diger sekmelerden gelen mesajlari dinler. */
export function listen(handler: (message: TabMessage) => void): () => void {
  const bus = get()
  if (!bus) return () => {}
  const onMessage = (event: MessageEvent<TabMessage>) => handler(event.data)
  bus.addEventListener('message', onMessage)
  return () => bus.removeEventListener('message', onMessage)
}

/** Testler icin: kanali kapatir. */
export function closeChannel(): void {
  channel?.close()
  channel = null
}

// ------------------------------------------------------------- lider secimi

export interface Leadership {
  /** Lider olundu mu. `false` ise akis ACILMAMALI. */
  readonly isLeader: boolean
  /** Liderligi birak (kilidi serbest birakir). */
  release(): void
}

/**
 * Bir run icin liderlik ister.
 *
 * `navigator.locks.request` ile: kilit sekme kapandiginda veya crash ettiginde
 * TARAYICI tarafindan serbest birakiliyor. Bunu `localStorage` ile elle
 * yapmak, olen bir sekmenin "ben liderim" kaydini geride birakmasi ve o run'in
 * bir daha HIC akmamasi demek olurdu - ve kurtarmasi yalnizca kaydin
 * eskidigini tahmin eden bir zaman asimi olurdu.
 *
 * Donus HEMEN geliyor: kilit alinamazsa `isLeader: false` ve cagiran
 * takipci olarak devam ediyor. Beklemiyoruz cunku bekleyen bir sekme, lider
 * kapandiginda uyanip akisa gec katilir - `after=lastSeq` bunu zaten
 * cozuyor ama gereksiz karmasiklik.
 */
export function requestLeadership(runId: string): Promise<Leadership> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (!locks) {
    // Web Locks yok: her sekme bagimsiz akitiyor. DOGRU, yalnizca israf.
    return Promise.resolve({ isLeader: true, release: () => {} })
  }

  return new Promise<Leadership>((resolve) => {
    let releaseLock: (() => void) | null = null
    let settled = false

    // `ifAvailable`: kilit mesgulse HEMEN `null` ile geri donuyor. Bu olmadan
    // istek KUYRUGA giriyor ve lider kapanana kadar bekliyor - yani takipci
    // sekme hicbir sey yapmadan asili kaliyor.
    locks
      .request(`run:${runId}`, { ifAvailable: true }, (lock) => {
        if (!lock) {
          if (!settled) {
            settled = true
            resolve({ isLeader: false, release: () => {} })
          }
          return
        }
        // Kilit TUTULUYOR: bu promise cozulene kadar. `release` cagrilinca
        // cozuluyor ve tarayici kilidi birakiyor.
        return new Promise<void>((releaseResolve) => {
          releaseLock = releaseResolve
          if (!settled) {
            settled = true
            resolve({
              isLeader: true,
              release: () => releaseLock?.(),
            })
          }
        })
      })
      .catch(() => {
        // Kilit istegi patlarsa lider SAYILIYORUZ: akisin hic acilmamasi,
        // iki kez acilmasindan cok daha kotu.
        if (!settled) {
          settled = true
          resolve({ isLeader: true, release: () => {} })
        }
      })
  })
}
